import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { habitsApi } from '../../api/habitsApi'
import type {
  CreateHabitRequest,
  HabitCompletionDto,
  HabitDto,
  HabitWeekDto,
  HabitsState,
} from '../../Models/Habit'
import { completionsDtoToByHabit, mapHabitDto } from '../../Models/Habit'

function errorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string } | undefined
    return data?.error ?? e.message ?? fallback
  }
  if (e instanceof Error) return e.message
  if (
    typeof e === 'object' &&
    e !== null &&
    'message' in e &&
    typeof (e as { message: unknown }).message === 'string'
  ) {
    return (e as { message: string }).message
  }
  return fallback
}

export const fetchHabits = createAsyncThunk<HabitDto[], void, { rejectValue: unknown }>(
  'habits/fetchAll',
  async (_, thunkAPI) => {
    try {
      return await habitsApi.getAll()
    } catch (error) {
      console.error(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const fetchHabitWeek = createAsyncThunk<HabitWeekDto, string, { rejectValue: unknown }>(
  'habits/fetchWeek',
  async (weekStart, thunkAPI) => {
    try {
      return await habitsApi.getWeek(weekStart)
    } catch (error) {
      console.error(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const createHabit = createAsyncThunk<HabitDto, CreateHabitRequest, { rejectValue: unknown }>(
  'habits/create',
  async (input, thunkAPI) => {
    try {
      return await habitsApi.create({
        title: input.title.trim(),
        category: input.category?.trim() || undefined,
        icon: input.icon,
      })
    } catch (error) {
      console.error(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const updateHabit = createAsyncThunk<
  HabitDto,
  { id: string; patch: Record<string, unknown> },
  { rejectValue: unknown }
>('habits/update', async ({ id, patch }, thunkAPI) => {
  try {
    return await habitsApi.update(id, patch)
  } catch (error) {
    console.error(error)
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteHabit = createAsyncThunk<string, string, { rejectValue: unknown }>(
  'habits/delete',
  async (id, thunkAPI) => {
    try {
      await habitsApi.delete(id)
      return id
    } catch (error) {
      console.error(error)
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const setHabitCompletion = createAsyncThunk<
  {
    habitId: string
    date: string
    completed: boolean
    weekStart: string
    dto: HabitCompletionDto | null
  },
  { habitId: string; date: string; completed: boolean; weekStart: string },
  { rejectValue: string }
>('habits/setCompletion', async (arg, thunkAPI) => {
  try {
    const dto = await habitsApi.setCompletion(arg.habitId, arg.date, arg.completed)
    return { ...arg, dto }
  } catch (error) {
    console.error(error)
    void thunkAPI.dispatch(fetchHabitWeek(arg.weekStart))
    return thunkAPI.rejectWithValue(errorMessage(error, 'Could not update habit completion.'))
  }
})

const initialState: HabitsState = {
  items: [],
  completionsByWeek: {},
  status: 'idle',
}

const habitsSlice = createSlice({
  name: 'habits',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHabits.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(fetchHabits.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.items = action.payload.map(mapHabitDto)
      })
      .addCase(fetchHabits.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to load habits.')
      })

      .addCase(fetchHabitWeek.pending, (state) => {
        state.error = undefined
      })
      .addCase(fetchHabitWeek.fulfilled, (state, action) => {
        const dto = action.payload
        state.completionsByWeek[dto.weekStart] = completionsDtoToByHabit(dto.completions)
      })
      .addCase(fetchHabitWeek.rejected, (state, action) => {
        state.error = errorMessage(action.payload ?? action.error, 'Failed to load habit week.')
      })

      .addCase(createHabit.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(createHabit.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const mapped = mapHabitDto(action.payload)
        const idx = state.items.findIndex((h) => h.id === mapped.id)
        if (idx >= 0) state.items[idx] = mapped
        else state.items.push(mapped)
      })
      .addCase(createHabit.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to create habit.')
      })

      .addCase(updateHabit.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(updateHabit.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const mapped = mapHabitDto(action.payload)
        const idx = state.items.findIndex((h) => h.id === mapped.id)
        if (idx >= 0) state.items[idx] = mapped
        else state.items.push(mapped)
      })
      .addCase(updateHabit.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to update habit.')
      })

      .addCase(deleteHabit.pending, (state) => {
        state.status = 'loading'
        state.error = undefined
      })
      .addCase(deleteHabit.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const id = action.payload
        state.items = state.items.filter((h) => h.id !== id)
        for (const w of Object.keys(state.completionsByWeek)) {
          const week = state.completionsByWeek[w]
          if (week[id]) {
            const { [id]: _removed, ...rest } = week
            state.completionsByWeek[w] = rest
          }
        }
      })
      .addCase(deleteHabit.rejected, (state, action) => {
        state.status = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to delete habit.')
      })

      .addCase(setHabitCompletion.pending, (state, action) => {
        const { weekStart, habitId, date, completed } = action.meta.arg
        const week = { ...(state.completionsByWeek[weekStart] ?? {}) }
        const dates = new Set(week[habitId] ?? [])
        if (completed) dates.add(date)
        else dates.delete(date)
        week[habitId] = Array.from(dates).sort()
        state.completionsByWeek[weekStart] = week
      })
      .addCase(setHabitCompletion.fulfilled, (state, action) => {
        const { weekStart, habitId, date, completed, dto } = action.payload
        const week = { ...(state.completionsByWeek[weekStart] ?? {}) }
        const dates = new Set(week[habitId] ?? [])
        if (completed && dto) {
          dates.add(dto.date)
        } else if (!completed) {
          dates.delete(date)
        }
        week[habitId] = Array.from(dates).sort()
        state.completionsByWeek[weekStart] = week
      })
      .addCase(setHabitCompletion.rejected, (state, action) => {
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : errorMessage(action.error, 'Could not update habit completion.')
      })
  },
})

export default habitsSlice.reducer
